import { Pipe, PipeTransform } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { SLATypeDetails } from '../../app-create-case/models/insurance-case.model';
import { CaseReportInfo } from '../models/domain/CaseReportInfo';

export interface SlaDaysLeftInfo {
    isOverdue: boolean;
    days: number;
    hours: number;
    label: string;
}

@Pipe({
    name: 'slaDaysLeft',
    standalone: false,
    pure: false
})
export class SlaDaysLeftPipe implements PipeTransform {
    constructor(private readonly translateService: TranslateService) {}

    transform(
        slaDetails: SLATypeDetails,
        caseReportList: CaseReportInfo[],
        readyForBillingDate: any
    ): SlaDaysLeftInfo | null {
        if (!slaDetails?.SLAExpiredDate || readyForBillingDate) {
            return null;
        }

        const expireDate = this.getAdjustedExpireDate(slaDetails.SLAExpiredDate);
        const diffMs = expireDate.getTime() - Date.now();

        if (diffMs <= 0) {
            const { days, hours } = this.getDurationParts(Math.abs(diffMs));

            return {
                isOverdue: true,
                days,
                hours,
                label: this.getOverdueLabel(days, hours)
            };
        }

        const { days, hours } = this.getDurationParts(diffMs);

        return {
            isOverdue: false,
            days,
            hours,
            label: this.getTimeLeftLabel(days, hours)
        };
    }

    private getDurationParts(diffMs: number): { days: number; hours: number } {
        const totalMinutes = Math.floor(diffMs / 60000);
        const days = Math.floor(totalMinutes / (60 * 24));
        const hours = Math.floor((totalMinutes % (60 * 24)) / 60);

        return { days, hours };
    }

    private getAdjustedExpireDate(slaExpiredDate: Date | string): Date {
        const expireDate = new Date(slaExpiredDate);
        const timezoneOffsetInMinutes = Math.abs(expireDate.getTimezoneOffset());
        expireDate.setMinutes(expireDate.getMinutes() - timezoneOffsetInMinutes);
        return expireDate;
    }

    private getOverdueLabel(days: number, hours: number): string {
        if (days > 0 && hours > 0) {
            return this.translateWithFallback('APP_CASES.SLA_DAYS_HOURS_OVERDUE', `${days} Days ${hours} Hours Overdue`, {
                days,
                hours
            });
        }

        if (days > 0) {
            if (days === 1) {
                return this.translateWithFallback('APP_CASES.SLA_DAY_OVERDUE', '1 Day Overdue');
            }

            return this.translateWithFallback('APP_CASES.SLA_DAYS_OVERDUE', `${days} Days Overdue`, { count: days });
        }

        if (hours > 0) {
            if (hours === 1) {
                return this.translateWithFallback('APP_CASES.SLA_HOUR_OVERDUE', '1 Hour Overdue');
            }

            return this.translateWithFallback('APP_CASES.SLA_HOURS_OVERDUE', `${hours} Hours Overdue`, { count: hours });
        }

        return this.translateWithFallback('APP_CASES.SLA_LESS_THAN_HOUR_OVERDUE', 'Less than 1 Hour Overdue');
    }

    private getTimeLeftLabel(days: number, hours: number): string {
        if (days > 0 && hours > 0) {
            return this.translateWithFallback('APP_CASES.SLA_DAYS_HOURS_LEFT', `${days} Days ${hours} Hours Left`, {
                days,
                hours
            });
        }

        if (days > 0) {
            if (days === 1) {
                return this.translateWithFallback('APP_CASES.SLA_DAY_LEFT', '1 Day Left');
            }

            return this.translateWithFallback('APP_CASES.SLA_DAYS_LEFT', `${days} Days Left`, { count: days });
        }

        if (hours > 0) {
            if (hours === 1) {
                return this.translateWithFallback('APP_CASES.SLA_HOUR_LEFT', '1 Hour Left');
            }

            return this.translateWithFallback('APP_CASES.SLA_HOURS_LEFT', `${hours} Hours Left`, { count: hours });
        }

        return this.translateWithFallback('APP_CASES.SLA_LESS_THAN_HOUR_LEFT', 'Less than 1 Hour Left');
    }

    private translateWithFallback(key: string, fallback: string, params?: Record<string, number>): string {
        const interpolationParams = this.toInterpolationParams(params);
        const translated = this.translateService.instant(key, interpolationParams);

        if (translated === key) {
            return this.interpolate(fallback, interpolationParams);
        }

        return this.interpolate(translated, interpolationParams);
    }

    private toInterpolationParams(params?: Record<string, number>): Record<string, string> | undefined {
        if (!params) {
            return undefined;
        }

        return Object.entries(params).reduce<Record<string, string>>((result, [paramKey, paramValue]) => {
            result[paramKey] = String(paramValue);
            return result;
        }, {});
    }

    private interpolate(text: string, params?: Record<string, string>): string {
        if (!params) {
            return text;
        }

        return Object.entries(params).reduce((result, [paramKey, paramValue]) => {
            return result.replace(new RegExp(`\\{\\{\\s*${paramKey}\\s*\\}\\}`, 'g'), paramValue);
        }, text);
    }
}
